// Central definition of the student enrollment classifications.
// Consumed by Create Student, Edit Student, Student Profile (badge),
// and any future filter or export. Kept as string literals so the API
// contract with `students.program_type` stays stable.
//
// Backend counterpart:
//   - migration extends the ENUM to accept these four values
//   - studentsController's validation rule uses the same list
//   - students model's getProgramLabelAttribute() returns the labels below
//
// Adding a fifth type in the future means: update this constant, the
// migration/enum, the validation rule, and the model label — nothing
// else in the UI needs to change because Create/Edit/View iterate over
// this array.

export interface StudentEnrollmentType {
  /** Persisted value on `students.program_type`. */
  value: string;
  /** Card title on Create/Edit + chip label on Profile. */
  title: string;
  /** Card subtitle on Create/Edit. */
  description: string;
  /** Muted MUI-palette-compatible tint used for the profile chip. */
  chipTintHex: string;
  /** Foreground text colour for the profile chip. */
  chipTextHex: string;
}

export const STUDENT_ENROLLMENT_TYPES: StudentEnrollmentType[] = [
  {
    value: "regular",
    title: "Regular Enrollment",
    description: "Standard classes and subjects",
    chipTintHex: "#22c55e", // green — matches the existing "Regular Program" tint
    chipTextHex: "#bbf7d0",
  },
  {
    value: "special",
    title: "ABA / Special Enrollment",
    description: "Specialized ABA programming",
    chipTintHex: "#a855f7", // purple — matches the existing "Special Program" tint
    chipTextHex: "#e9d5ff",
  },
  {
    value: "occupational_therapy",
    title: "Occupational Therapy",
    description: "Occupational therapy services and programming",
    chipTintHex: "#0d9488", // teal
    chipTextHex: "#ccfbf1",
  },
  {
    value: "speech_therapy",
    title: "Speech Therapy",
    description: "Speech and language therapy services and programming",
    chipTintHex: "#2563eb", // blue
    chipTextHex: "#dbeafe",
  },
];

/** Radio-card options for the Create/Edit forms (matches RadioCardOption shape). */
export const STUDENT_ENROLLMENT_RADIO_OPTIONS = STUDENT_ENROLLMENT_TYPES.map(
  (t) => ({ value: t.value, title: t.title, description: t.description }),
);

/** Fast lookup by persisted value; used by the Profile badge. */
export const studentEnrollmentTypeByValue = (v: string | null | undefined): StudentEnrollmentType | undefined =>
  v ? STUDENT_ENROLLMENT_TYPES.find((t) => t.value === v) : undefined;

/** Human label for a stored value. Falls back to a Title-Cased slug so
 *  a legacy or unknown value never renders as raw `snake_case`. */
export const studentEnrollmentTypeLabel = (v: string | null | undefined): string => {
  if (!v) return "";
  const known = studentEnrollmentTypeByValue(v);
  if (known) return known.title;
  return v.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};
